// src/components/PruebasList.jsx
// Lista de pruebas (diagnóstico o simulacros) con contador automático
// que muestra “Comienza en X horas, Y minutos, Z segundos” y habilita
// el botón exactamente cuando llega la hora de apertura.

import { useEffect, useState } from "react";
import { useNavigate }        from "react-router-dom";
import api                    from "../api";

/* —— Utilidad para formatear fechas, ej.: “22 de junio de 2025 (9:00 a. m.)” —— */
const formatFecha = (iso) => {
  const f = new Date(iso);
  const fecha = f.toLocaleDateString("es-PE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const hora = f.toLocaleTimeString("es-PE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${fecha} (${hora})`;
};

/* —— Convierte segundos → “7 horas, 33 minutos, 50 segundos” —— */
const toHumanDuration = (totalSeg) => {
  const h = Math.floor(totalSeg / 3600);
  const m = Math.floor((totalSeg % 3600) / 60);
  const s = totalSeg % 60;
  const chunks = [];
  if (h > 0) chunks.push(`${h} hora${h > 1 ? "s" : ""}`);
  if (m > 0) chunks.push(`${m} minuto${m > 1 ? "s" : ""}`);
  chunks.push(`${s} segundo${s !== 1 ? "s" : ""}`);
  return chunks.join(", ");
};

export default function PruebasList({ tipo }) {
  const [lista, setLista] = useState(null);
  const [err,   setErr]   = useState("");
  const [now,   setNow]   = useState(() => Date.now());      // reloj global
  const nav               = useNavigate();

  /* —— Reloj: refresca cada segundo —— */
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  /* —— Traer lista una vez —— */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/pruebas", { params: { tipo } });
        if (alive) setLista(data);
      } catch (e) {
        console.error(e);
        if (alive) setErr("No se pudo cargar la lista de pruebas.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [tipo]);

  /* —— Render condicionales básicos —— */
  if (err)        return <p className="text-red-600">{err}</p>;
  if (!lista)     return <p>Cargando…</p>;
  if (!lista.length)
    return <p className="text-gray-600">No hay pruebas disponibles.</p>;

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {lista.map((p) => {
        const { id } = p;

        /* Fechas de apertura / cierre */
        const inicio  = new Date(p.fecha_apertura).getTime();
        const cierre  = p.fecha_cierre ? new Date(p.fecha_cierre).getTime() : null;

        const habilitado = now >= inicio && (!cierre || now < cierre);
        const expirado   = cierre && now >= cierre;
        const faltanSeg  = Math.max(0, Math.floor((inicio - now) / 1000));

        return (
          <div key={id} className="border rounded-xl p-6 shadow bg-white">
            <h3 className="text-lg font-semibold mb-2">
              {p.titulo || `${p.tipo} ${p.numero}`}
            </h3>

            <p className="text-sm">
              Disponible desde:&nbsp;{formatFecha(p.fecha_apertura)}
            </p>
            {p.fecha_cierre && (
              <p className="text-sm mb-2">
                Cierre:&nbsp;{formatFecha(p.fecha_cierre)}
              </p>
            )}

            {/* Estado */}
            {!habilitado && !expirado && (
              <span className="text-yellow-600 font-medium">
                Comienza en {toHumanDuration(faltanSeg)}
              </span>
            )}
            {expirado && (
              <span className="text-red-600 font-medium">Cerrada</span>
            )}

            {/* Botón */}
            <button
              onClick={() => nav(`/app/pruebas/${id}`)}
              disabled={!habilitado}
              className={`mt-4 w-full px-4 py-2 rounded-full transition
                ${habilitado
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
            >
              {expirado ? "Finalizado" : "Rendir prueba"}
            </button>
          </div>
        );
      })}
    </div>
  );
}
